import { useState } from 'react';
import type { MouseEvent } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { alpha, useColorScheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import AccountCircleOutlined from '@mui/icons-material/AccountCircleOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { usePreferencesDialog } from '../profile/components/PreferencesDialogProvider';
import { msalInstance } from '../auth/msalConfig';
import { markSignedOut } from '../auth/authStatus';
import { useWhoami, whoamiQueryKey } from '../api/whoami';
import { useToast } from '../components';
import { AdminImpersonationDialog } from './AdminImpersonationDialog';
import { SupportedLanguages } from '../i18n/config';
import type { SupportedLanguage } from '../i18n/config';
import { useUserLanguage } from '../i18n/useUserLanguage';

interface SidebarAccountMenuProps {
  /** i18n key for the workspace label under the user's name (from `whoami`). */
  workspaceLabelKey: string;
}

/**
 * Role-chip tone. Resolved to colors at render rather than returned as literal
 * hexes, so the `primary` tone tracks the customer's brand color (WEB PRD §2.6).
 * `accent` (Administrator) and `neutral` stay DecisionPoint's — the orange accent
 * has no customer token, and the neutral chip is deliberately brand-agnostic.
 */
type RoleBadgeTone = 'accent' | 'primary' | 'neutral';

function roleBadge(
  isAdministrator: boolean,
  isManager: boolean,
  isRequestDelegate: boolean,
): { labelKey: string; tone: RoleBadgeTone } {
  if (isAdministrator) {
    return { labelKey: 'userMenu.roles.administrator', tone: 'accent' };
  }
  if (isManager) {
    return { labelKey: 'userMenu.roles.manager', tone: 'primary' };
  }
  if (isRequestDelegate) {
    return { labelKey: 'userMenu.roles.requestDelegate', tone: 'primary' };
  }
  return { labelKey: 'userMenu.roles.user', tone: 'neutral' };
}

/** Chip fill for a tone. `primary` is derived so it follows the brand color. */
function roleBadgeBg(tone: RoleBadgeTone, theme: Theme): string {
  if (tone === 'accent') return alpha(theme.palette.secondary.main, 0.12);
  if (tone === 'primary') return alpha(theme.palette.primary.main, 0.1);
  return 'rgba(4,29,51,0.06)';
}

/** Chip label color for a tone. `primary` is derived so it follows the brand color. */
function roleBadgeFg(tone: RoleBadgeTone, theme: Theme): string {
  if (tone === 'accent') return theme.palette.secondary.dark;
  if (tone === 'primary') return theme.palette.primary.dark;
  return '#435666';
}

/**
 * The signed-in user's account control, docked at the BOTTOM of the navy rail
 * (prototype chrome — replaces the former top-right avatar menu). A dark trigger
 * pill opens an upward light popover: identity + role badge, My profile,
 * Preferences, Language, View-as-user (Administrator), theme toggle, Sign out.
 */
export function SidebarAccountMenu({ workspaceLabelKey }: SidebarAccountMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const preferencesDialog = usePreferencesDialog();
  const { mode, setMode } = useColorScheme();
  const { language, setLanguage } = useUserLanguage();
  const { data: whoami } = useWhoami();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const [viewAsUserOpen, setViewAsUserOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const email = whoami?.effective.email ?? '';
  const initial = (email || '?').charAt(0).toUpperCase();
  const isAdmin = whoami?.effective.isAdministrator ?? false;
  const isImpersonating = whoami?.isImpersonating ?? false;
  // "View as user" is offered only to an Administrator who is not already
  // impersonating — nested impersonation is blocked server-side, so the item is
  // hidden while a session is active (the effective identity may still be an
  // Administrator when the target holds that role).
  const canImpersonate = isAdmin && !isImpersonating;
  const badge = roleBadge(
    isAdmin,
    whoami?.effective.isManager ?? false,
    whoami?.effective.isRequestDelegate ?? false,
  );
  const isDark = mode === 'dark';

  const closeMenu = () => setAnchorEl(null);

  const handleLanguage = async (next: SupportedLanguage) => {
    closeMenu();
    await setLanguage(next);
    await queryClient.invalidateQueries({ queryKey: whoamiQueryKey });
  };

  const handleMyProfile = () => {
    closeMenu();
    navigate('/profile');
  };

  const handlePreferences = () => {
    closeMenu();
    if (preferencesDialog) {
      preferencesDialog.open();
    } else {
      navigate('/profile');
    }
  };

  const handleThemeToggle = () => {
    setMode(isDark ? 'light' : 'dark');
  };

  const handleViewAsUser = () => {
    closeMenu();
    setViewAsUserOpen(true);
  };

  const handleSignOut = async () => {
    // Keep the menu open so the "Signing you out…" pending state is visible;
    // the logout redirect tears down the page a moment later.
    setSigningOut(true);
    try {
      await apiClient.post('/auth/sign-out');
    } catch {
      // Best-effort server cleanup (audit + auto-end impersonation); proceed to
      // the identity-provider logout regardless of the server's response.
    }
    queryClient.removeQueries({ queryKey: whoamiQueryKey });
    markSignedOut();
    try {
      await msalInstance.logoutRedirect();
    } catch {
      setSigningOut(false);
      toast.error(t('errors.generic'));
      // MSAL clears its cache before navigating, so a logout that fails to
      // redirect leaves us inside `RequireAuth` with no account — which would
      // immediately bounce the user to Microsoft (DDR-PLT-024) and swallow the
      // toast. Land them on the public, non-redirecting sign-in page instead,
      // where the error is readable and they can retry deliberately.
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'brand.sidebarDivider',
        p: 1.5,
        flexShrink: 0,
      }}
    >
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t('userMenu.open')}
        onClick={(event: MouseEvent<HTMLElement>) => {
          // Capture the element now — `currentTarget` is nulled by the time the
          // functional state updater runs.
          const trigger = event.currentTarget;
          setAnchorEl((prev) => (prev ? null : trigger));
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          p: 1,
          border: 'none',
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: 'transparent',
          '&:hover': { bgcolor: 'brand.sidebarTriggerHoverBg' },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {initial}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <Typography
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 600,
              fontSize: 14,
              lineHeight: 1.2,
              color: 'brand.sidebarFg',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email}
          </Typography>
          <Typography
            sx={{
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 400,
              fontSize: 12,
              lineHeight: 1.2,
              mt: '2px',
              color: 'brand.sidebarFgMuted',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {t(workspaceLabelKey)}
          </Typography>
        </Box>
        <KeyboardArrowUp fontSize="small" sx={{ color: 'brand.sidebarFgMuted' }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 268, borderRadius: '10px', mb: 1 } } }}
      >
        <Box sx={{ px: 1.25, pt: 1, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: 'primary.main',
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
              }}
            >
              {initial}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 600,
              }}
            >
              {email}
            </Typography>
          </Box>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              mt: 1,
              px: 1,
              py: '2px',
              borderRadius: 999,
              bgcolor: (theme) => roleBadgeBg(badge.tone, theme),
              color: (theme) => roleBadgeFg(badge.tone, theme),
              fontFamily: (theme) => theme.typography.button.fontFamily,
              fontWeight: 700,
              fontSize: 10,
              lineHeight: 1.6,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {t(badge.labelKey)}
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={handleMyProfile}>
          <ListItemIcon>
            <AccountCircleOutlined fontSize="small" />
          </ListItemIcon>
          {t('userMenu.myProfile')}
        </MenuItem>
        <MenuItem onClick={handlePreferences}>
          <ListItemIcon>
            <TuneOutlined fontSize="small" />
          </ListItemIcon>
          {t('userMenu.preferences')}
        </MenuItem>
        <Divider />
        <ListSubheader disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2 }}>
          {t('nav.language')}
        </ListSubheader>
        {SupportedLanguages.map((lng) => (
          <MenuItem key={lng} selected={lng === language} onClick={() => handleLanguage(lng)}>
            <ListItemIcon>
              {lng === language ? (
                <CheckOutlined fontSize="small" sx={{ color: 'success.main' }} />
              ) : (
                <TranslateOutlined fontSize="small" />
              )}
            </ListItemIcon>
            {t(`languages.${lng}`)}
          </MenuItem>
        ))}
        <Divider />
        {canImpersonate && (
          <MenuItem onClick={handleViewAsUser}>
            <ListItemIcon>
              <VisibilityOutlined fontSize="small" />
            </ListItemIcon>
            <Box sx={{ flex: 1 }}>{t('userMenu.viewAsUser')}</Box>
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 0.75,
                py: '2px',
                borderRadius: '4px',
                bgcolor: 'action.hover',
                color: 'text.secondary',
                fontFamily: (theme) => theme.typography.button.fontFamily,
                fontWeight: 600,
                fontSize: 9,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              {t('userMenu.adminBadge')}
            </Box>
          </MenuItem>
        )}
        <MenuItem onClick={handleThemeToggle}>
          <ListItemIcon>
            {isDark ? (
              <LightModeOutlined fontSize="small" />
            ) : (
              <DarkModeOutlined fontSize="small" />
            )}
          </ListItemIcon>
          {isDark ? t('userMenu.lightMode') : t('userMenu.darkMode')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut} disabled={signingOut} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            {signingOut ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <LogoutOutlined fontSize="small" sx={{ color: 'error.main' }} />
            )}
          </ListItemIcon>
          {signingOut ? t('signOut.pending') : t('nav.signOut')}
        </MenuItem>
      </Menu>

      {viewAsUserOpen && <AdminImpersonationDialog onClose={() => setViewAsUserOpen(false)} />}
    </Box>
  );
}
